import { createRouter, createWebHistory } from "vue-router";
import store from "../store";

/*
|--------------------------------------------------------------------------
| Auth / Public Routes
|--------------------------------------------------------------------------
*/

const authRoutes = [
    {
        path: "/",
        name: "login",
        component: () => import("@/views/auth/SignInView.vue"),
        meta: { guest: true, title: "Login" },
    },
    {
        path: "/reset-password/:token",
        name: "reset-password",
        component: () => import("@/views/auth/ResetPasswordView.vue"),
        meta: { guest: true, title: "Reset Password" },
    },
    {
        path: "/error/page-not-found",
        name: "not-found",
        component: () => import("@/views/auth/PageNotFoundView.vue"),
        meta: { title: "Page Not Found" },
    },
];

/*
|--------------------------------------------------------------------------
| Dashboard / Profile
|--------------------------------------------------------------------------
*/

const dashboardRoutes = [
    {
        path: "/home",
        name: "home",
        component: () => import("@/views/HomeView.vue"),
        meta: { login: true, title: "Dashboard" },
    },
    {
        path: "/profile",
        name: "profile",
        component: () => import("@/views/ProfileView.vue"),
        meta: { login: true, title: "Profile" },
    },
    {
        path: "/load",
        name: "load",
        component: () => import("@/views/LoadView.vue"),
        meta: { login: true, title: "Load" },
    },
];

/*
|--------------------------------------------------------------------------
| PR / PO / Contract
|--------------------------------------------------------------------------
*/

const procurementRoutes = [
    {
        path: "/pr",
        name: "pr",
        component: () => import("@/views/procurement/PRView.vue"),
        meta: { login: true, title: "Purchase Request" },
    },
    {
        path: "/pr-analytics",
        name: "pr-analytics",
        component: () => import("@/views/Analytics/PrAnalytics.vue"),
        meta: { login: true, title: "PR Analytics" },
    },
    {
        path: "/pr-detail/:id",
        name: "pr-detail",
        component: () => import("@/views/procurement/PRDetailView.vue"),
        meta: { login: true, title: "PR Detail" },
    },
    {
        path: "/approved-pr",
        name: "approved-pr",
        component: () => import("@/views/procurement/POView.vue"),
        meta: { login: true, title: "Approved PR" },
    },
    {
        path: "/po/:id",
        name: "po",
        component: () => import("@/views/procurement/POPRView.vue"),
        meta: { login: true, title: "PO" },
    },
    {
        path: "/po-list",
        name: "po-list",
        component: () => import("@/views/procurement/POListView.vue"),
        meta: { login: true, title: "PO List" },
    },
    {
        path: "/po-detail/:id",
        name: "po-detail",
        component: () => import("@/views/procurement/PODetailView.vue"),
        meta: { login: true, title: "PO Detail" },
    },
    {
        path: "/create-po/:id",
        name: "create-po",
        component: () => import("@/views/procurement/CreatePOView.vue"),
        meta: { login: true, title: "Create PO" },
    },
    {
        path: "/create-po-manual/:id",
        name: "create-po-manual",
        component: () => import("@/views/procurement/CreatePOManualView.vue"),
        meta: { login: true, title: "Create PO Manual" },
    },
    {
        path: "/contract",
        name: "contract",
        component: () => import("@/views/procurement/ContractView.vue"),
        meta: { login: true, title: "Contract" },
    },
    {
        path: "/create-contract",
        name: "create-contract",
        component: () => import("@/views/procurement/CreateContractView.vue"),
        meta: { login: true, title: "Create Contract" },
    },
    {
        path: "/contract-detail/:id",
        name: "contract-detail",
        component: () => import("@/views/procurement/ContractDetailView.vue"),
        meta: { login: true, title: "Contract Detail" },
    },
];

/*
|--------------------------------------------------------------------------
| Master Data
|--------------------------------------------------------------------------
*/

const masterRoutes = [
    {
        path: "/masterbarang",
        name: "masterbarang",
        component: () => import("@/views/master/MasterBarangView.vue"),
        meta: { login: true, title: "Master Barang" },
    },
    {
        path: "/jenis",
        name: "jenis",
        component: () => import("@/views/master/MasterJenisView.vue"),
        meta: { login: true, title: "Master Jenis" },
    },
    {
        path: "/pricelist-supplier",
        name: "pricelist-supplier",
        component: () => import("@/views/master/PricelistBarangView.vue"),
        meta: { login: true, title: "Pricelist Supplier" },
    },
    {
        path: "/conversion",
        name: "conversion",
        component: () => import("@/views/master/ManageConversionView.vue"),
        meta: { login: true, title: "Conversion" },
    },
    {
        path: "/units",
        name: "units",
        component: () => import("@/views/master/ManageUnitView.vue"),
        meta: { login: true, title: "Manage Units" },
    },
    {
        path: "/master-container",
        name: "master-container",
        component: () => import("@/views/master/MasterContainerView.vue"),
        meta: { login: true, title: "Master Container" },
    },
];

/*
|--------------------------------------------------------------------------
| Receiving / Invoice / Billing / Payment
|--------------------------------------------------------------------------
*/

const receivingRoutes = [
    {
        path: "/create-receiving",
        name: "create-receiving",
        component: () => import("@/views/receiving/CreateReceivingView.vue"),
        meta: { login: true, title: "Create Receiving" },
    },
    {
        path: "/receiving",
        name: "receiving",
        component: () => import("@/views/receiving/ReceivingView.vue"),
        meta: { login: true, title: "Receiving" },
    },
    {
        path: "/receiving-manual",
        name: "receiving-manual",
        component: () => import("@/views/receiving/ReceivingManualView.vue"),
        meta: { login: true, title: "Receiving Manual" },
    },
    {
        path: "/receivedetails/:no_karcis",
        name: "receivingdetails",
        component: () => import("@/views/receiving/ReceivingDetailView.vue"),
        meta: { login: true, title: "Receiving Detail" },
    },
    {
        path: "/itemdistributions/:no_karcis/:no_po/:kd_barang",
        name: "itemdistributions",
        component: () => import("@/views/receiving/ReceivingItemsRelation.vue"),
        meta: { login: true, title: "Item Distribution Relation" },
    },
    {
        path: "/invoice-exchanges",
        name: "invoice-exchanges",
        component: () => import("@/views/receiving/InvoiceExchange.vue"),
        meta: { login: true, title: "Invoice Exchange" },
    },
    {
        path: "/invoice-cancel",
        name: "invoice-cancel",
        component: () => import("@/views/receiving/CancelInvoiceView.vue"),
        meta: { login: true, title: "Cancel Invoice" },
    },
    {
        path: "/invoice-exchange-posting",
        name: "invoice-exchange-posting",
        component: () => import("@/views/receiving/InvoiceExchangePosting.vue"),
        meta: { login: true, title: "Invoice Exchange Posting" },
    },
    {
        path: "/invoice-exchange-posting-detail/:tt_no/:sup_kd",
        name: "invoice-exchange-posting-detail",
        component: () => import("@/views/receiving/InvoiceExchangePostingDetail.vue"),
        meta: { login: true, title: "Invoice Exchange Posting Detail" },
    },
    {
        path: "/invoice-exchange-report",
        name: "invoice-exchange-report",
        component: () => import("@/views/receiving/InvoiceExchangeReport.vue"),
        meta: { login: true, title: "Invoice Exchange Report" },
    },
    {
        path: "/billing-receiving",
        name: "billing receiving",
        component: () => import("@/views/receiving/BillingReceivingView.vue"),
        meta: { login: true, title: "Billing Receiving" },
    },
    {
        path: "/payment-information",
        name: "payment-information",
        component: () => import("@/views/receiving/PaymentInformation.vue"),
        meta: { login: true, title: "Payment Information" },
    },
    {
        path: "/payment-information-detail/:tt_no",
        name: "payment-information-detail",
        component: () => import("@/views/receiving/PaymentInformationDetail.vue"),
        meta: { login: true, title: "Payment Information Detail" },
    },
    {
        path: "/upload-sj-invoice",
        name: "upload receiving",
        component: () => import("@/views/receiving/UploadReceiving.vue"),
        meta: { login: true, title: "Upload Receiving" },
    },
];

/*
|--------------------------------------------------------------------------
| Request / Item Distribution / Tracking
|--------------------------------------------------------------------------
*/

const requestRoutes = [
    {
        path: "/tracking-view",
        name: "tracking-view",
        component: () => import("@/views/request/TrackingView.vue"),
        meta: { login: true, title: "Tracking" },
    },
    {
        path: "/request-items",
        name: "request-items",
        component: () => import("@/views/request/RequestAssetView.vue"),
        meta: { login: true, title: "Request Items" },
    },
    {
        path: "/request-stok",
        name: "request-stok",
        component: () => import("@/views/request/DistribusiStokView.vue"),
        meta: { login: true, title: "Request Stok" },
    },
    {
        path: "/request-detail-stok/:req_no",
        name: "request-detail-stok",
        component: () => import("@/views/request/DistribusiStokDetailView.vue"),
        meta: { login: true, title: "Request Detail Stok" },
    },
    {
        path: "/items-distribution",
        name: "items-distribution",
        component: () => import("@/views/request/ItemsDistributionView.vue"),
        meta: { login: true, title: "Items Distribution" },
    },
    {
        path: "/items-distribution-list",
        name: "items-distribution-list",
        component: () => import("@/views/request/ItemsDistributionList.vue"),
        meta: { login: true, title: "Items Distribution List" },
    },
    {
        path: "/items-distribution-details/:dist_no",
        name: "items-distribution-details",
        component: () => import("@/views/request/ItemsDistributionDetail.vue"),
        meta: { login: true, title: "Items Distribution Detail" },
    },
];

/*
|--------------------------------------------------------------------------
| Reporting
|--------------------------------------------------------------------------
*/

const reportingRoutes = [
    {
        path: "/dashboard-reporting",
        name: "dashboard-reporting",
        component: () => import("@/views/reporting/DashbreportingView.vue"),
        meta: { login: true, title: "Dashboard Reporting" },
    },
    {
        path: "/financeReport",
        name: "financeReport",
        component: () => import("@/views/reporting/FinanceReport.vue"),
        meta: { login: true, title: "Finance Report" },
    },
    {
        path: "/reporting",
        name: "reporting",
        component: () => import("@/views/reporting/ReportingView.vue"),
        meta: { login: true, title: "Reporting" },
    },
];

/*
|--------------------------------------------------------------------------
| Stock Dashboard / Finance Stock
|--------------------------------------------------------------------------
*/

const stockFinanceRoutes = [
    {
        path: "/dashboardStock",
        name: "dashboardStock",
        component: () => import("@/views/stock/DashbView.vue"),
        meta: { login: true, title: "Dashboard Stock" },
    },
    {
        path: "/stock",
        name: "stock",
        component: () => import("@/views/stock/FinanceStock/StockView.vue"),
        meta: { login: true, title: "Stock" },
    },
    {
        path: "/stock-detail/:kd_barang/:kd_gdg/:purchase_unit",
        name: "StockDetailView",
        component: () => import("@/views/stock/FinanceStock/StockDetail.vue"),
        meta: { login: true, title: "Stock Detail" },
    },
    {
        path: "/stock-detail-all/:kd_barang",
        name: "StockDetailAllView",
        component: () =>
            import("@/views/stock/FinanceStock/StockDetailAllView.vue"),
        meta: { login: true, title: "Stock Detail All" },
    },
    {
        path: "/stock/:kd_barang/:kd_gdg/day/:period/:day",
        name: "StockDayDetail",
        component: () =>
            import("@/views/stock/FinanceStock/StockDayDetail.vue"),
        meta: { login: true, title: "Stock Day Detail" },
    },
    {
        path: "/stock-period-management",
        name: "stock-period-management",
        component: () =>
            import(
                "@/views/stock/PeriodeManagement/StockPeriodManagementView.vue"
            ),
        meta: { login: true, title: "Stock Period Management" },
    },
];

/*
|--------------------------------------------------------------------------
| Marketlist Stock
|--------------------------------------------------------------------------
*/

const stockMarketlistRoutes = [
    {
        path: "/stock-marketlist",
        name: "stock-marketlist",
        component: () =>
            import("@/views/stock/MarketlistStock/StockMarketlistView.vue"),
        meta: { login: true, title: "Stock Marketlist" },
    },
    {
        path: "/purchase-marketlist-nonstock/:kd_barang",
        name: "purchase-marketlist-nonstock-detail",
        component: () =>
            import(
                "@/views/stock/MarketlistStock/StockMarketlistDetailView.vue"
            ),
        meta: { login: true, title: "Stock Detail Marketlist" },
    },
    {
        path: "/purchase-marketlist-nonstock/:kd_barang/kartu-stok",
        name: "purchase-marketlist-nonstock-kartu-stok",
        component: () =>
            import("@/views/stock/MarketlistStock/StockMarketlistCardView.vue"),
        meta: { login: true, title: "Kartu Stok Marketlist" },
    },
    {
        path: "/marketlist/:kd_barang/:lok_kd/day/:period/:day",
        name: "MarketDayDetail",
        component: () =>
            import("@/views/stock/MarketlistStock/MarketlistDayDetailView.vue"),
        meta: { login: true, title: "Marketlist Day Detail" },
    },
];

/*
|--------------------------------------------------------------------------
| Internal Transfer
|--------------------------------------------------------------------------
*/

const InternalTransferRoutes = [
    {
        path: "/marketlist/internal-transfer",
        name: "MarketlistInternalTransfer",
        component: () =>
            import(
                "@/views/stock/InternalTransfer/InternalTransferListView.vue"
            ),
        meta: { login: true, title: "Internal Transfer" },
    },
    {
        path: "/marketlist/internal-transfer/create",
        name: "marketlist-internal-transfer-create",
        component: () =>
            import(
                "@/views/stock/InternalTransfer/InternalTransferCreateView.vue"
            ),
        meta: { login: true, title: "Create Internal Transfer" },
    },
    {
        path: "/marketlist/internal-transfer/packages",
        name: "marketlist-internal-transfer-packages",
        component: () =>
            import(
                "@/views/stock/InternalTransfer/InternalTransferPackageListView.vue"
            ),
        meta: { login: true, title: "Package Transfer" },
    },
    {
        path: "/marketlist/internal-transfer/packages/create",
        name: "marketlist-internal-transfer-package-create",
        component: () =>
            import(
                "@/views/stock/InternalTransfer/InternalTransferPackageFormView.vue"
            ),
        meta: { login: true, title: "Create Package Transfer" },
    },
    {
        path: "/marketlist/internal-transfer/packages/:id",
        name: "marketlist-internal-transfer-package-edit",
        component: () =>
            import(
                "@/views/stock/InternalTransfer/InternalTransferPackageFormView.vue"
            ),
        meta: { login: true, title: "Edit Package Transfer" },
    },
    {
        path: "/marketlist/internal-transfer/:transfer_number",
        name: "marketlist-internal-transfer-detail",
        component: () =>
            import(
                "@/views/stock/InternalTransfer/InternalTransferDetailView.vue"
            ),
        meta: { login: true, title: "Internal Transfer Detail" },
    },
];

/*
|--------------------------------------------------------------------------
| Stock Opname
|--------------------------------------------------------------------------
*/

const stockOpnameRoutes = [
    {
        path: "/stock-opname-finance",
        name: "stock-opname",
        component: () =>
            import("@/views/stock/StockOpname/StockOpnameFinance.vue"),
        meta: { login: true, title: "Stock Opname" },
    },

    {
        path: "/stock-opname-marketlist",
        name: "stock-opname-marketlist",
        component: () =>
            import(
                "@/views/stock/StockOpname/StockOpnameMarketlistSelectView.vue"
            ),
        meta: { login: true, title: "Stock Opname Marketlist" },
    },
    {
        path: "/stock-opname-marketlist/input",
        name: "stock-opname-marketlist-input",
        component: () =>
            import("@/views/stock/StockOpname/StockOpnameMarketlist.vue"),
        meta: { login: true, title: "Input Stock Opname Marketlist" },
    },
    {
        path: "/stock-opname-marketlist/monthly",
        name: "stock-opname-marketlist-monthly",
        component: () =>
            import(
                "@/views/stock/StockOpname/StockOpnameMarketlistMonthlyView.vue"
            ),
        meta: { login: true, title: "Monthly Stock Opname Marketlist" },
    },
    {
        path: "/stock-opname-data",
        name: "stock-opname-data",
        component: () => import("@/views/stock/StockOpname/OpnameView.vue"),
        meta: { login: true, title: "Stock Opname Data" },
    },
    {
        path: "/stock-opname-data-marketlist",
        name: "stock-opname-data-marketlist",
        component: () =>
            import("@/views/stock/StockOpname/OpnameMarketlist.vue"),
        meta: { login: true, title: "Stock Opname Marketlist Data" },
    },
    {
        path: "/stock-opname-detail/:no_opname",
        name: "stock-opname-detail",
        component: () =>
            import("@/views/stock/StockOpname/OpnameDetailView.vue"),
        meta: { login: true, title: "Stock Opname Detail" },
    },
];

/*
|--------------------------------------------------------------------------
| Stock Movement: Request / Waste / Transfer / Posting
|--------------------------------------------------------------------------
*/

const stockMovementRoutes = [
    {
        path: "/request-stock",
        name: "request-stock",
        component: () => import("@/views/stock/RequestStock/RequestStock.vue"),
        meta: { login: true, title: "Request Stock" },
    },
    {
        path: "/add-request-stock",
        name: "add-request-stock",
        component: () =>
            import("@/views/stock/RequestStock/AddRequestStock.vue"),
        meta: { login: true, title: "Add Request Stock" },
    },
    {
        path: "/request-stock-details/:id",
        name: "request-stock-details",
        component: () =>
            import("@/views/stock/RequestStock/RequestStockDetails.vue"),
        meta: { login: true, title: "Request Stock Details" },
    },
    {
        path: "/waste-stock-view",
        name: "waste-stock-view",
        component: () => import("@/views/stock/WasteStock/WasteView.vue"),
        meta: { login: true, title: "Waste Stock" },
    },
    {
        path: "/waste-stock",
        name: "waste-stock",
        component: () => import("@/views/stock/WasteStock/WasteStock.vue"),
        meta: { login: true, title: "Waste Stock" },
    },
    {
        path: "/waste-stock-detail/:no_waste",
        name: "waste-stock-detail",
        component: () => import("@/views/stock/WasteStock/WasteDetailView.vue"),
        meta: { login: true, title: "Waste Stock Detail" },
    },
    {
        path: "/posting-stock",
        name: "posting-stock",
        component: () => import("@/views/stock/batch/IndexBatch.vue"),
        meta: { login: true, title: "Posting Stock" },
    },
    {
        path: "/transfer-stock",
        name: "transfer-stock",
        component: () =>
            import("@/views/stock/TransferStock/TransferStock.vue"),
        meta: { login: true, title: "Transfer Stock" },
    },
    {
        path: "/transfer-gudang",
        name: "transfer-gudang",
        component: () =>
            import("@/views/stock/TransferStock/TransferGudang.vue"),
        meta: { login: true, title: "Transfer Gudang" },
    },
    {
        path: "/list-transfer-finance",
        name: "list-transfer-finance",
        component: () =>
            import("@/views/stock/TransferStock/ListTransferFinance.vue"),
        meta: { login: true, title: "List Transfer Finance" },
    },
    {
        path: "/list-transfer-gudang",
        name: "list-transfer-gudang",
        component: () =>
            import("@/views/stock/TransferStock/ListTransferGudang.vue"),
        meta: { login: true, title: "List Transfer Gudang" },
    },
    {
        path: "/transfer-detail/:stktrfno",
        name: "transfer-detail",
        component: () =>
            import("@/views/stock/TransferStock/DetailTransferGudang.vue"),
        meta: { login: true, title: "Transfer Stock Detail" },
    },
];

/*
|--------------------------------------------------------------------------
| Retur
|--------------------------------------------------------------------------
*/

const returRoutes = [
    {
        path: "/return",
        name: "ReturView",
        component: () => import("@/views/Retur/ReturView.vue"),
        meta: { login: true, title: "Retur" },
    },
    {
        path: "/return-detail/:no_retur",
        name: "ReturDetailView",
        component: () => import("@/views/Retur/ReturDetail.vue"),
        meta: { login: true, title: "Retur Detail" },
    },
];

/*
|--------------------------------------------------------------------------
| Fallback
|--------------------------------------------------------------------------
*/

const fallbackRoutes = [
    {
        path: "/:pathMatch(.*)*",
        name: "error",
        component: () => import("@/views/auth/PageNotFoundView.vue"),
        meta: { title: "Page Not Found" },
    },
];

/*
|--------------------------------------------------------------------------
| Register Routes
|--------------------------------------------------------------------------
*/

const appLayoutRoute = {
    path: "/",
    component: () => import("@/components/AppLayout.vue"),
    children: [
        ...dashboardRoutes,
        ...procurementRoutes,
        ...masterRoutes,
        ...receivingRoutes,
        ...requestRoutes,
        ...reportingRoutes,
        ...stockFinanceRoutes,
        ...stockMarketlistRoutes,
        ...InternalTransferRoutes,
        ...stockOpnameRoutes,
        ...stockMovementRoutes,
        ...returRoutes,
    ],
};

const routes = [
    ...authRoutes,
    appLayoutRoute,
    ...fallbackRoutes,
];

const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes,
});

/*
|--------------------------------------------------------------------------
| Global Guard
|--------------------------------------------------------------------------
*/

router.beforeEach((to, from, next) => {
    window.scrollTo(0, 0);

    if (to.name === "login") {
        try {
            const logoutMessage = sessionStorage.getItem("logout_message");
            sessionStorage.clear();

            if (logoutMessage) {
                sessionStorage.setItem("logout_message", logoutMessage);
            }
        } catch {
            //
        }
    }

    const needLogin = to.matched.some((record) => record.meta.login);
    const guestOnly = to.matched.some((record) => record.meta.guest);
    const hasToken = Boolean(store.getters.GET_AUTH_TOKEN);

    if (needLogin && !hasToken) {
        next({ name: "not-found" });
        return;
    }

    if (guestOnly && hasToken) {
        next({ name: "home" });
        return;
    }

    next();
});

/*
|--------------------------------------------------------------------------
| Page Title
|--------------------------------------------------------------------------
*/

router.afterEach((to) => {
    const title = to.meta?.title;

    document.title = title ? `${title} | Procurement` : "Procurement";
});

export default router;
